import { toast } from '@approvalmax/ui/src/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { selectors, statics } from 'modules/common';
import { useDispatch, useSelector } from 'modules/react-redux';
import { useMyTimeZone } from 'modules/utils';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useCreateDelegations, useGetCompanyDelegations } from 'shared/data/webApp/v2';
import { delegationHelpers } from 'shared/helpers';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import { cancelActivePopup, setOptimisticDelegate } from '../../actions';
import type { SetDelegatePopupData } from '../../reducers/page/activePopupReducer';
import { getActivePopup } from '../../selectors/pageSelectors';
import { messages } from './SetDelegatePopupContent.messages';
import { schema } from './SetDelegatePopupContent.schema';
import type { FormValues } from './SetDelegatePopupContent.types';

export const useSetDelegatePopupContent = () => {
    const dispatch = useDispatch();
    const company = useCompanyBasedOnUrl();
    const myTimeZone = useMyTimeZone();

    const { data: delegations, isLoading: isLoadingDelegations } = useGetCompanyDelegations({
        path: { companyId: company.id },
    });
    const { mutateAsync: updateDelegations, isLoading: isLoadingUpdateDelegations } = useCreateDelegations();

    const team = useSelector((state) => selectors.company.getCompanyTeam(state, company));
    const userId = useSelector((state) => getActivePopup<SetDelegatePopupData>(state)).userId;

    const user = useMemo(() => team.find((user) => user.id === userId), [team, userId]);
    const initialValues = useMemo(() => {
        const delegates = delegations?.delegates || [];
        const delegation = delegates.find((delegate) => delegate.fromUser.userId === user?.databaseId);
        const myTz = statics.timeZone.findTimeZoneById(myTimeZone);
        const myTzUtcOffset = statics.timeZone.getUtcOffset(myTz);

        return {
            delegateId: delegation?.toUser?.email ?? null,
            dateFrom: delegation?.dateFrom
                ? statics.timeZone.addOffset(delegation.dateFrom, myTzUtcOffset).toDate()
                : null,
            dateTo: delegation?.dateTo ? statics.timeZone.addOffset(delegation.dateTo, myTzUtcOffset).toDate() : null,
            originalDateFrom: delegation?.dateFrom,
            originDateTo: delegation?.dateTo,
        };
    }, [delegations?.delegates, myTimeZone, user?.databaseId]);

    const form = useForm<FormValues>({
        values: {
            timeZone: myTimeZone,
            ...initialValues,
        },
        resolver: zodResolver(schema),
    });
    const {
        formState: { touchedFields },
    } = form;
    const formValues = form.watch();

    const nowDateWithOffset = useMemo(
        () => delegationHelpers.getNowAtTimeZone(formValues.timeZone),
        [formValues.timeZone]
    );

    const dateFromMinDate = nowDateWithOffset;
    const dateFromMaxDate = formValues.dateTo ?? undefined;
    const dateToMinDate = formValues.dateFrom ?? nowDateWithOffset;

    const delegatesOptions = useMemo(
        () =>
            team
                .filter((teamUser) => teamUser.id !== userId)
                .sort(selectors.user.userComparatorAsc)
                .map((teamUser) => ({ ...teamUser, name: teamUser.displayName })),
        [team, userId]
    );

    const delegateCurrent = useMemo(
        () => team.find((delegateUser) => delegateUser.id === formValues.delegateId),
        [formValues.delegateId, team]
    );

    const handleDelegateRemove = useCallback(() => {
        form.resetField('delegateId', { defaultValue: null });
        form.resetField('dateFrom', { defaultValue: null });
        form.resetField('dateTo', { defaultValue: null });
        form.resetField('timeZone', { defaultValue: myTimeZone });
    }, [form, myTimeZone]);

    const handleSubmit = form.handleSubmit((values) => {
        if (!user) {
            return;
        }

        const delegateTimeZone = values.timeZone ? statics.timeZone.findTimeZoneById(values.timeZone) : undefined;
        const selectedTzOffset = statics.timeZone.getUtcOffset(delegateTimeZone);

        const dateFrom = statics.timeZone.subtractOffset(values.dateFrom, selectedTzOffset).toISOString();
        const dateTo = statics.timeZone.subtractOffset(values.dateTo, selectedTzOffset).toISOString();

        if (
            values.delegateId === initialValues.delegateId &&
            !delegationHelpers.compareDates(dateFrom, initialValues.originalDateFrom) &&
            !delegationHelpers.compareDates(dateTo, initialValues.originDateTo)
        ) {
            dispatch(cancelActivePopup());

            return;
        }

        updateDelegations({
            body: [
                {
                    companyId: company.id,
                    delegates: [
                        {
                            fromUser: { userId: user.databaseId || user.id },
                            ...(delegateCurrent
                                ? {
                                      toUser: { userId: delegateCurrent.databaseId || delegateCurrent.id },
                                      dateFrom,
                                      dateTo,
                                  }
                                : {}),
                        },
                    ],
                },
            ],
        })
            .then(() => {
                toast.success(
                    delegateCurrent
                        ? messages.delegateSetNotificationMessage
                        : messages.delegateClearNotificationMessage
                );

                dispatch(
                    setOptimisticDelegate({
                        companyId: company.id,
                        user,
                        delegateUserId: delegateCurrent ? delegateCurrent.id : undefined,
                        delegateUserProfileId: delegateCurrent ? delegateCurrent.databaseId : undefined,
                        delegateDateFrom: dateFrom,
                        delegateDateTo: dateTo,
                    })
                );
            })
            .catch(() => {});
    });

    form.register('delegateId', {
        onChange: (event) => {
            if (event.type === 'change') {
                if (!formValues.dateFrom) {
                    // this is always your profile tz, so no need to shift
                    const myTz = statics.timeZone.findTimeZoneById(myTimeZone);
                    const myTzUtcOffset = statics.timeZone.getUtcOffset(myTz);

                    form.setValue('dateFrom', statics.timeZone.addOffset(new Date(), myTzUtcOffset).toDate());
                }
            }
        },
    });

    form.register('timeZone', {
        onChange: (event) => {
            if (event.target.value) {
                // changing time zones within this field is a read-only action. We shouldn't change
                // the real dates because of that. The purpose of that is to show to user the time
                // in a time zone of his delegate.
                //
                // We decided to not modify DatePicker for that, but do a real form modifications and
                // later during SUBMIT do a revert modifications
                const oldTz = formValues.timeZone ? statics.timeZone.findTimeZoneById(formValues.timeZone) : undefined;
                const newTz = statics.timeZone.findTimeZoneById(event.target.value);
                const oldUtcOffset = statics.timeZone.getUtcOffset(oldTz);
                const newUtcOffset = statics.timeZone.getUtcOffset(newTz);
                const tzShift = statics.timeZone.getOffsetShift(newUtcOffset, oldUtcOffset);

                if (formValues.dateFrom) {
                    form.setValue('dateFrom', statics.timeZone.addOffset(formValues.dateFrom, tzShift).toDate(), {
                        shouldTouch: false,
                    });
                }

                if (formValues.dateTo) {
                    form.setValue('dateTo', statics.timeZone.addOffset(formValues.dateTo, tzShift).toDate(), {
                        shouldTouch: false,
                    });
                }
            }
        },
    });

    form.register('dateFrom', {
        onChange: (event) => {
            // can't compare w/ original because user can change field
            // several times and eventually back to the original
            if (event.target.value && touchedFields.dateFrom) {
                // reset `isTouched` state to prevent setValue on `timeZone` field change
                form.resetField('dateFrom', {
                    defaultValue: event.target.value,
                });
            }
        },
    });

    form.register('dateTo', {
        onChange: (event) => {
            if (event.target.value) {
                if (event.target.value && touchedFields.dateTo) {
                    // reset `isTouched` state to prevent setValue on `timeZone` field change
                    form.resetField('dateTo', {
                        defaultValue: event.target.value,
                    });
                }

                if (!formValues.dateFrom) {
                    form.setValue('dateFrom', nowDateWithOffset);
                }
            }
        },
    });

    return {
        companyId: company.id,
        form,
        user,
        delegateCurrent,
        delegatesOptions,
        dateFromMinDate,
        dateFromMaxDate,
        dateToMinDate,
        handleSubmit,
        handleDelegateRemove,
        disabled: !formValues.delegateId,
        progress: isLoadingDelegations || isLoadingUpdateDelegations,
    };
};
